import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, X } from "lucide-react";
import OnboardingWizard from "./OnboardingWizard";

/**
 * Post-signup gentle prompt: "Would you like to add your first vendor now?"
 * Auto-shows only when: (1) user JUST landed on Dashboard, (2) ZERO vendors
 * exist yet, (3) not previously dismissed. Adding one vendor bumps the setup
 * checklist from 17% → 33%+.
 */
export default function SignupVendorPrompt() {
  const KEY = "signup_vendor_prompt_dismissed_v1";
  const [show, setShow] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (localStorage.getItem(KEY) === "1") return;
    api.get("/parties", { params: { party_type: "vendor" } }).then(r => {
      if (Array.isArray(r.data) && r.data.length === 0) setShow(true);
    }).catch(() => {});
  }, []);

  const hide = () => { localStorage.setItem(KEY, "1"); setShow(false); };
  if (!show) return null;

  return (
    <>
      <Card className="rounded-sm border border-accent/30 bg-gradient-to-r from-accent/10 to-transparent p-4 md:p-5 relative"
              data-testid="signup-vendor-prompt">
        <button onClick={hide}
                className="absolute top-2 right-2 text-muted-foreground/60 hover:text-foreground p-1"
                aria-label="Dismiss" data-testid="svp-dismiss">
          <X className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-center gap-3 flex-wrap md:flex-nowrap">
          <div className="w-11 h-11 rounded-sm bg-accent/20 flex items-center justify-center text-accent shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// 30-second next step</div>
            <div className="font-heading font-semibold text-lg md:text-xl mt-0.5 leading-tight">
              Want to add your first vendor now?
            </div>
            <div className="text-xs md:text-sm text-muted-foreground mt-1">
              We'll auto-verify their GSTIN, infer PAN, suggest the right TDS section, and score them 300-900. Adding one takes ~30 seconds and jumps your setup checklist forward.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" size="sm" onClick={hide} className="rounded-sm text-muted-foreground">Later</Button>
            <Button onClick={() => setWizardOpen(true)}
                      className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5 h-10 px-4" data-testid="svp-cta">
              Add vendor <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      <OnboardingWizard open={wizardOpen} onOpenChange={setWizardOpen} partyType="vendor"
                          onCreated={() => { hide(); nav("/app/parties/vendors"); }} />
    </>
  );
}
